/**
 * UpgradeModal Component
 * The paywall moment — appears when a user swipes to a locked model in the
 * carousel, or taps a gated feature.
 *
 * Design inspired by Robinhood Gold upgrade screen:
 * - Hero gradient with the locked feature preview (blurred)
 * - Plan comparison cards (Free → PRO → Enterprise)
 * - Active plan highlighted with ring
 * - One-tap "Upgrade to PRO" CTA
 * - "Try 7 days free" trial badge
 * - Feature list with checkmarks
 *
 * Uses the existing Modal component for structure + accessibility.
 *
 * Firebreak Design System v4:
 * - Modal: glass-panel with dark mode support
 * - CTA gradient: blue (light) / amber (dark) - fire.ember accent
 * - Feature checks: text-status-approved (green)
 */

import { useMemo } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { cn } from '../utils/cn';
import {
  useSubscription,
  PLANS,
  type SubscriptionTier,
  type SubscriptionPlan,
} from '../contexts/SubscriptionContext';
import { useHaptics } from '../hooks/useHaptics';

// ============================================================================
// Constants
// ============================================================================

const TIER_GRADIENTS: Record<SubscriptionTier, string> = {
  free: 'from-slate-400 to-slate-500 dark:from-slate-600 dark:to-slate-700',
  pro: 'from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800',
  enterprise: 'from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800',
};

const TIER_RING_COLORS: Record<SubscriptionTier, string> = {
  free: 'ring-slate-300 dark:ring-slate-700',
  pro: 'ring-gray-900 dark:ring-gray-900',
  enterprise: 'ring-gray-900 dark:ring-gray-400',
};

const TIER_BG: Record<SubscriptionTier, string> = {
  free: 'bg-slate-50 dark:bg-slate-900',
  pro: 'bg-gray-50 dark:bg-gray-950/20',
  enterprise: 'bg-gray-50 dark:bg-gray-950/20',
};

// ============================================================================
// Sub-components
// ============================================================================

function PlanCard({
  plan,
  isCurrent,
  isTarget,
  onSelect,
}: {
  plan: SubscriptionPlan;
  isCurrent: boolean;
  isTarget: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={cn(
        'relative rounded-xl border-2 p-4 transition-all duration-200',
        isTarget
          ? `${TIER_RING_COLORS[plan.tier]} border-current ring-2 scale-[1.02]`
          : isCurrent
            ? 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900'
            : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
      )}
    >
      {/* Current badge */}
      {isCurrent && (
        <span className="absolute -top-2.5 left-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-600 rounded-full dark:bg-slate-700 dark:text-slate-300">
          Current plan
        </span>
      )}

      {/* Recommended badge */}
      {plan.highlighted && isTarget && (
        <span className={cn(
          'absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full',
          'bg-gradient-to-r', TIER_GRADIENTS[plan.tier], 'text-white'
        )}>
          Recommended
        </span>
      )}

      <div className="text-center mb-3">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{plan.name}</h3>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{plan.tagline}</p>
        <div className="mt-2">
          <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{plan.price}</span>
          {plan.priceMonthly > 0 && (
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">/ month</span>
          )}
        </div>
      </div>

      {/* Feature list */}
      <ul className="space-y-1.5 mb-4">
        {plan.features.map((feature, i) => {
          const isHeader = feature.endsWith(':');
          return (
            <li
              key={feature}
              className={cn(
                'flex items-start gap-1.5 text-[11px]',
                isHeader ? 'font-semibold text-slate-700 dark:text-slate-200 mt-1' : 'text-slate-600 dark:text-slate-300'
              )}
            >
              {!isHeader && (
                <svg
                  className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
              <span>{feature}</span>
            </li>
          );
        })}
      </ul>

      {/* Select button */}
      {!isCurrent && (
        <button
          type="button"
          onClick={onSelect}
          className={cn(
            'w-full py-2 px-3 rounded-lg text-xs font-semibold transition-all duration-200',
            isTarget
              ? `bg-gradient-to-r ${TIER_GRADIENTS[plan.tier]} text-white shadow-lg hover:shadow-xl`
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
          )}
        >
          {plan.priceMonthly === -1
            ? 'Contact Sales'
            : plan.priceMonthly === 0
              ? 'Downgrade'
              : `Upgrade to ${plan.name}`
          }
        </button>
      )}

      {isCurrent && (
        <div className="w-full py-2 px-3 rounded-lg text-xs font-medium text-center text-slate-400 bg-slate-100 dark:bg-slate-800 dark:text-slate-500">
          Active
        </div>
      )}
    </div>
  );
}

function FeaturePreview({ context }: { context: string | null }) {
  if (!context) return null;

  return (
    <div className="relative overflow-hidden rounded-xl mb-4">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/80 dark:to-slate-900/80 z-10" />
      <div className="bg-slate-900/90 dark:bg-slate-800/90 backdrop-blur-lg p-4 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-gray-700/20 dark:bg-gray-700/30 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{context}</span>
        </div>
        {/* Blurred preview bars — teaser of the locked content */}
        <div className="space-y-2 filter blur-[2px]">
          <div className="h-3 bg-gray-700/20 rounded w-3/4" />
          <div className="h-3 bg-gray-700/15 rounded w-full" />
          <div className="h-3 bg-gray-700/10 rounded w-2/3" />
          <div className="h-8 bg-gray-700/20 rounded w-1/2 mt-3" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function UpgradeModal() {
  const {
    tier: currentTier,
    showUpgradeModal,
    upsellTier,
    upsellContext,
    dismissUpgrade,
    simulateUpgrade,
  } = useSubscription();
  const haptics = useHaptics();

  const targetTier = upsellTier ?? 'pro';
  const targetPlan = PLANS.find(p => p.tier === targetTier);

  const handleUpgrade = (plan: SubscriptionPlan) => {
    haptics.select();
    if (plan.priceMonthly === -1) {
      // Enterprise — open contact form / external link
      window.open('https://firebreakrisk.ai/enterprise', '_blank');
      return;
    }
    // Demo: instant upgrade simulation
    simulateUpgrade(plan.tier);
  };

  return (
    <Modal
      isOpen={showUpgradeModal}
      onClose={dismissUpgrade}
      size="lg"
    >
      {/* Hero section */}
      <div className="text-center mb-6">
        <div className={cn(
          'inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3',
          'bg-gradient-to-br', TIER_GRADIENTS[targetTier]
        )}>
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Unlock {targetPlan?.name ?? 'Firebreak Pro'}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {targetPlan?.tagline ?? 'Advanced risk analysis'}
        </p>

        {/* Trial badge */}
        {targetTier === 'pro' && (
          <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-gray-50 dark:bg-slate-800 shadow-sm shadow-gray-900/5">
            <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-xs font-medium text-gray-700 dark:text-slate-300">7-day free trial</span>
          </div>
        )}
      </div>

      {/* Feature preview (what you tried to access) */}
      <FeaturePreview context={upsellContext} />

      {/* Plan comparison */}
      <div className="grid grid-cols-3 gap-3">
        {PLANS.map(plan => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            isCurrent={plan.tier === currentTier}
            isTarget={plan.tier === targetTier}
            onSelect={() => handleUpgrade(plan)}
          />
        ))}
      </div>

      {/* Footer note */}
      <div className="text-center mt-4 space-y-1">
        <p className="text-[10px] text-slate-400 dark:text-slate-500">
          Cancel anytime. No long-term commitment.
        </p>
        <p className="text-[10px] text-slate-400 dark:text-slate-500">
          Trusted by 200+ insurers and reinsurers worldwide.
        </p>
      </div>
    </Modal>
  );
}

export default UpgradeModal;
